import { ResultOf } from "@graphql-typed-document-node/core";
import { format, formatDuration } from "date-fns";
import { uniqBy } from "lodash";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "urql";

import { FullPageSpinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { FragmentType, getFragmentData, graphql } from "@/gen";
import { generateImageUrl } from "@/utils/image";

const useVideos_Video = graphql(`
    fragment useVideos_Video on Video {
        id
        ...VideosTable_Video
    }
`);

const useVideos = ({ currentPage }: { currentPage: number }) => {
    const [videos, setVideos] = useState<ResultOf<typeof useVideos_Video>[]>([]);

    const offset = currentPage * 50;

    const [result, refetch] = useQuery({
        query: graphql(`
            query videosQuery($input: VideosInput!) {
                videos(input: $input) {
                    items {
                        id
                        ...useVideos_Video
                    }
                    total
                    hasMore
                }
            }
        `),
        variables: {
            input: {
                limit: 50,
                offset,
            },
        },
    });

    useEffect(() => {
        if (result.data?.videos?.items) {
            const videos = getFragmentData(useVideos_Video, result.data.videos.items);
            setVideos(uniqBy(videos ?? [], "id"));
        }
    }, [result.data?.videos.items]);

    return {
        videos,
        hasMore: result.data?.videos?.hasMore ?? false,
        count: result.data?.videos?.total ?? null,
        pageCounts: result.data?.videos?.total ? Math.ceil(result.data.videos.total / 50) : null,
        loading: result.fetching,
        error: result.error,
        refetch,
    };
};

const useVideosParams = () => {
    const [params, setParams] = useSearchParams();

    const updateParam = useCallback(
        (key: string, value?: string, replace?: boolean) => {
            if (value) {
                params.set(key, value);
            } else {
                params.delete(key);
            }
            setParams(params, { replace });
        },
        [params, setParams]
    );

    return {
        pageNumber: parseInt(params.get("page") ?? "0", 10),
        setPageNumber: useCallback(
            (page: number) => updateParam("page", page.toString(), true),
            [updateParam]
        ),
    };
};

const VideoRoute = () => {
    const params = useVideosParams();

    const { videos, hasMore, loading, error } = useVideos({
        currentPage: params.pageNumber,
    });

    if (loading) {
        return <FullPageSpinner />;
    }

    if (error) {
        return <div className="mb-4 w-full space-y-4 py-4">Error {error.message}</div>;
    }

    return (
        <Tabs defaultValue="all" className="m-4">
            <TabsContent value="all">
                <VideosTable videoFragments={videos} loading={loading} hasMore={hasMore} />
            </TabsContent>
        </Tabs>
    );
};

const VideosTable_Video = graphql(`
    fragment VideosTable_Video on Video {
        id
        title
        thumbnailImageId
        createdAt
        aspectRatio
        duration
        muxStatus
    }
`);

export function VideosTable({
    videoFragments,
    loading,
    hasMore,
}: {
    videoFragments: FragmentType<typeof VideosTable_Video>[];
    loading: boolean;
    hasMore: boolean;
}) {
    const videos = getFragmentData(VideosTable_Video, videoFragments);
    const navigate = useNavigate();
    const params = useVideosParams();

    const videosRef = useRef<HTMLTableSectionElement>(null);

    const scrollToTop = (ref: RefObject<HTMLTableSectionElement>) => {
        ref.current?.scroll({
            top: 0,
        });
    };

    const prevButtonDisabled = loading || params.pageNumber === 0;
    const nextButtonDisabled = loading || !hasMore;

    return (
        <Card className="bg-brand-white text-brand-black rounded border-none">
            <CardHeader>
                <CardTitle>Videos</CardTitle>
            </CardHeader>
            <CardContent>
                <Button onClick={() => navigate(`/videos/create`)}>Create +</Button>
                <Table>
                    <TableHeader>
                        <TableRow className="hover:bg-brand-gray-light">
                            <TableHead className="text-brand-gray-dark">Title</TableHead>
                            <TableHead className="text-brand-gray-dark">Thumbnail</TableHead>
                            <TableHead className="text-brand-gray-dark">Status</TableHead>
                            <TableHead className="text-brand-gray-dark">Duration</TableHead>
                            <TableHead className="text-brand-gray-dark">Date</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody ref={videosRef}>
                        {videos.map(video => (
                            <TableRow
                                className="hover:bg-brand-purple cursor-pointer hover:bg-opacity-20"
                                key={video.id}
                                onClick={() => navigate(`/videos/${video.id}`)}
                            >
                                <TableCell className="font-medium">{video.title}</TableCell>
                                <TableCell className="font-medium">
                                    <span className="text-sm">
                                        {video.thumbnailImageId ? (
                                            <img
                                                src={generateImageUrl({
                                                    imageId: video.thumbnailImageId,
                                                    height: 30,
                                                    width: 30,
                                                })}
                                                className="object-contain"
                                                alt="Video thumbnail"
                                            />
                                        ) : (
                                            <span>Missing thumb</span>
                                        )}
                                    </span>
                                </TableCell>
                                <TableCell className="font-medium">{video.muxStatus}</TableCell>

                                <TableCell className="font-medium">
                                    {video.duration
                                        ? formatDuration({ seconds: +video.duration.toFixed(2) })
                                        : "-"}
                                </TableCell>
                                <TableCell className="font-medium">
                                    {format(new Date(video.createdAt), "MM/dd/yyyy")}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
            <Pagination className="my-4">
                <PaginationContent>
                    {!prevButtonDisabled && (
                        <>
                            <PaginationItem
                                onClick={() => {
                                    scrollToTop(videosRef);
                                    params.setPageNumber(Math.max(0, params.pageNumber - 1));
                                }}
                            >
                                <PaginationPrevious />
                            </PaginationItem>

                            <PaginationItem
                                onClick={() => {
                                    scrollToTop(videosRef);
                                    params.setPageNumber(Math.max(0, params.pageNumber - 1));
                                }}
                            >
                                <PaginationLink>{params.pageNumber}</PaginationLink>
                            </PaginationItem>
                        </>
                    )}
                    <PaginationItem>
                        <PaginationLink href="#" isActive>
                            {params.pageNumber + 1}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem
                        onClick={() => {
                            scrollToTop(videosRef);
                            params.setPageNumber(params.pageNumber + 1);
                        }}
                    >
                        {!nextButtonDisabled && (
                            <PaginationLink href="#">{params.pageNumber + 2}</PaginationLink>
                        )}
                    </PaginationItem>
                    {!nextButtonDisabled && (
                        <>
                            <PaginationItem>
                                <PaginationEllipsis />
                            </PaginationItem>
                            <PaginationItem
                                onClick={() => {
                                    scrollToTop(videosRef);
                                    params.setPageNumber(params.pageNumber + 1);
                                }}
                            >
                                <PaginationNext />
                            </PaginationItem>
                        </>
                    )}
                </PaginationContent>
            </Pagination>
        </Card>
    );
}

export default VideoRoute;
